import { API_ROOT, ApiError, http, type Fetch } from './http';
import { SignInRequired } from './savedDesigns';
import {
  areaName,
  NotAFitter,
  type FitterAccount,
  type FitterState,
  type TradeBid,
  type TradeBidStatus,
  type TradeJob,
  type TradeJobs,
  type TradeJobStatus,
} from './tradeJobs';

/**
 * The live `TradeJobs`: the signed-in fitter through the storefront's own
 * routes (contract `commerce/docs/TRADE_AND_INSTALLER_JOBS_CONTRACT.md` §5–§6).
 * Where they stand, and the areas they cover, are the account profile's
 * `installer` field, read at `/api/storefront/account/me` and saved there as
 * `installerAreas`; the jobs and quotes stay at `/api/storefront/installers/me/jobs`.
 *
 * <p>These are plain storefront routes, not Spatial extension routes: installers
 * are a commerce feature both shops share, so nothing here goes through
 * `/extensions/spatial`.
 *
 * <p>Errors: 401/403, and a profile read that says nobody is signed in, become
 * `SignInRequired`; a profile without an `installer`, and a 404 whose kind is
 * `NOT_INSTALLER`, become {@link NotAFitter}. Any other failure keeps the
 * backend's sentence (an `ApiError`), which the blocks show as it is.
 */
export const INSTALLER_ME = `${API_ROOT}/installers/me`;
/** The signed-in shopper's profile, with the `installer` standing commerce adds. */
export const ACCOUNT_ME = `${API_ROOT}/account/me`;

/** `/account/me` as the storefront answers it: null when nobody is signed in. */
export interface AccountMeDto {
  email?: string;
  installer?: InstallerMeDto | null;
}

/** `InstallerMeDto`, as the backend sends it. */
export interface InstallerMeDto {
  state: string;
  businessName?: string | null;
  trades?: string[] | null;
  areas?: string[] | null;
  canQuote?: boolean | null;
  blockers?: string[] | null;
  documentsLink?: string | null;
  /** `APPLIED` only: policy types whose certificate has not been uploaded yet. */
  certificatesOutstanding?: string[] | null;
}

/** An applicant whose application still waits for a certificate: the blocker the account page shows. */
export const CERTIFICATES_OUTSTANDING = 'CERTIFICATES_OUTSTANDING';

/** `InstallerJobDto`, as the backend sends it. */
export interface InstallerJobDto {
  id: string | number;
  reference?: string | null;
  title: string;
  district: string;
  areaName?: string | null;
  trades?: string[] | null;
  scope?: string[] | null;
  summary?: string | null;
  readyFrom: string;
  budgetGuide?: string | null;
  closesAt?: string | null;
  packNotes?: string | null;
  postedAt: string;
  status: string;
  quotes?: number | null;
  sitePostcode?: string | null;
  myQuote?: {
    id: string | number;
    price: number | string;
    availableFrom: string;
    note?: string | null;
    status: string;
    sentAt: string;
  } | null;
}

const FITTER_STATES: readonly FitterState[] = ['APPLIED', 'ACTIVE', 'SUSPENDED', 'REJECTED'];
const JOB_STATUSES: readonly TradeJobStatus[] = ['open', 'awarded', 'closed', 'cancelled'];
const BID_STATUSES: readonly TradeBidStatus[] = ['sent', 'withdrawn', 'accepted', 'declined'];

const strings = (value: string[] | null | undefined): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.trim() !== '') : [];

const optional = (value: string | null | undefined): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

export function toFitterAccount(dto: InstallerMeDto): FitterAccount {
  const state = String(dto.state ?? '').toUpperCase() as FitterState;
  return {
    // An unknown state is treated as one that cannot quote, never as ACTIVE.
    state: FITTER_STATES.includes(state) ? state : 'APPLIED',
    businessName: dto.businessName?.trim() ?? '',
    trades: strings(dto.trades),
    postcodes: strings(dto.areas).map((code) => code.toUpperCase().replace(/\s+/g, '')),
    canQuote: dto.canQuote === true,
    // Certificates an application sent with the sign-up still lacks stop it being reviewed at all.
    blockers: [...strings(dto.blockers), ...(strings(dto.certificatesOutstanding).length ? [CERTIFICATES_OUTSTANDING] : [])],
    documentsLink: optional(dto.documentsLink) ?? null,
  };
}

export function toTradeJob(dto: InstallerJobDto): TradeJob {
  const id = String(dto.id);
  const outward = String(dto.district ?? '').toUpperCase().replace(/\s+/g, '');
  const status = String(dto.status ?? '').toLowerCase() as TradeJobStatus;
  const quote = dto.myQuote;
  let myBid: TradeBid | undefined;
  if (quote) {
    const bidStatus = String(quote.status ?? '').toLowerCase() as TradeBidStatus;
    myBid = {
      id: String(quote.id),
      jobId: id,
      price: Number(quote.price),
      availableFrom: quote.availableFrom,
      note: quote.note ?? '',
      status: BID_STATUSES.includes(bidStatus) ? bidStatus : 'sent',
      sentAt: quote.sentAt,
    };
  }
  const reference = optional(dto.reference);
  const budgetGuide = optional(dto.budgetGuide);
  const closesAt = optional(dto.closesAt);
  const packNotes = optional(dto.packNotes);
  const sitePostcode = optional(dto.sitePostcode);
  return {
    id,
    ...(reference ? { reference } : {}),
    title: dto.title,
    outward,
    // The backend leaves naming to the storefront; Royal Mail's name for the area.
    areaLabel: optional(dto.areaName) ?? areaName(outward),
    scope: strings(dto.scope),
    trades: strings(dto.trades),
    kitchenSummary: dto.summary?.trim() ?? '',
    readyFrom: dto.readyFrom,
    ...(budgetGuide ? { budgetGuide } : {}),
    ...(closesAt ? { closesAt } : {}),
    ...(packNotes ? { packNotes } : {}),
    postedAt: dto.postedAt,
    bids: typeof dto.quotes === 'number' && dto.quotes >= 0 ? dto.quotes : 0,
    // An unknown status takes no quotes.
    status: JOB_STATUSES.includes(status) ? status : 'closed',
    ...(sitePostcode ? { sitePostcode } : {}),
    ...(myBid ? { myBid } : {}),
  };
}

/** The refusal's kind: the envelope carries it as `developerMessage` (or `errorKey`/`code` on older shapes). */
function kindOf(body: unknown): string | undefined {
  const b = body as { developerMessage?: unknown; errorKey?: unknown; code?: unknown; error?: unknown } | null;
  for (const value of [b?.developerMessage, b?.errorKey, b?.code, b?.error]) {
    if (typeof value === 'string' && /^[A-Z][A-Z_]+$/.test(value)) return value;
  }
  return undefined;
}

export function liveTradeJobs(fetchImpl: Fetch = (input, init) => fetch(input, init)): TradeJobs {
  // A 404 NOT_INSTALLER is read before `http` turns the answer into an ApiError,
  // because the envelope's kind is not one of the fields ApiError keeps.
  const peeking: Fetch = async (input, init) => {
    const response = await fetchImpl(input, init);
    if (response.status === 404) {
      const body: unknown = await response.clone().json().catch(() => null);
      if (kindOf(body) === 'NOT_INSTALLER') throw new NotAFitter();
    }
    return response;
  };
  const api = http(peeking);
  const refused = (error: unknown): never => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) throw new SignInRequired();
    throw error;
  };
  const job = (id: string) => `${INSTALLER_ME}/jobs/${encodeURIComponent(id)}`;
  /** The profile's installer, or the refusal a missing one means. */
  const installerOf = (profile: AccountMeDto | null): InstallerMeDto => {
    if (!profile) throw new SignInRequired();
    if (!profile.installer) throw new NotAFitter();
    return profile.installer;
  };

  return {
    async getAccount() {
      return toFitterAccount(installerOf(await api.get<AccountMeDto | null>(ACCOUNT_ME).catch(refused)));
    },
    async saveAreas(postcodes) {
      const profile = await api.send<AccountMeDto | null>('PUT', ACCOUNT_ME, { installerAreas: postcodes }).catch(refused);
      return toFitterAccount(installerOf(profile));
    },
    async jobs() {
      const list = await api.get<InstallerJobDto[] | null>(`${INSTALLER_ME}/jobs`).catch(refused);
      return (list ?? []).map(toTradeJob);
    },
    async placeBid(jobId, bid) {
      const body = { price: bid.price, availableFrom: bid.availableFrom, note: bid.note };
      return toTradeJob(await api.send<InstallerJobDto>('POST', `${job(jobId)}/quote`, body).catch(refused));
    },
    async withdrawBid(jobId) {
      return toTradeJob(await api.send<InstallerJobDto>('POST', `${job(jobId)}/withdraw`).catch(refused));
    },
  };
}
